/**
 * Client-side PDF export for ODP cards.
 *
 * Two approaches:
 * 1. pdf-lib direct draw (vector PDF, selectable text, needs font embedding)
 * 2. Canvas render → pdf-lib embed (raster PDF, simpler, no font embedding)
 */

import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import fontkit from "@pdf-lib/fontkit";
import type { CardInput } from "./types.js";
import {
  LEFT_ZONE, RIGHT_ZONE,
  PAGE_WIDTH_IN, PAGE_HEIGHT_IN,
  BLUE_FILL, ORANGE_FILL, TITLE_COLOR, BODY_COLOR, PILL_TEXT_COLOR,
  PILL_FONT_SIZE_IN, TITLE_FONT_SIZE_IN, TITLE_POS,
  parseIn, pillLayout,
} from "./layout.js";

// PDF uses points: 1 inch = 72 points
const PPI = 72;
const PW = PAGE_WIDTH_IN * PPI;
const PH = PAGE_HEIGHT_IN * PPI;

function hexToRgb(hex: string) {
  const h = hex.replace("#", "");
  return rgb(
    parseInt(h.slice(0, 2), 16) / 255,
    parseInt(h.slice(2, 4), 16) / 255,
    parseInt(h.slice(4, 6), 16) / 255,
  );
}

function inPt(inches: number): number { return inches * PPI; }
// PDF Y is bottom-up; convert from top-down ODP Y
function flipY(topY: number, height: number): number { return PH - topY * PPI - height * PPI; }

function parseDesc(text: string): { text: string; bold: boolean }[] {
  const parts: { text: string; bold: boolean }[] = [];
  const re = /「([^」]+)」/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push({ text: text.slice(last, m.index), bold: false });
    parts.push({ text: m[0], bold: true });
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push({ text: text.slice(last), bold: false });
  return parts;
}

// ---- Approach 1: pdf-lib direct draw (vector) ----

export async function exportPdfDirect(
  cards: { card: CardInput; zone: "left" | "right"; imageUrls: string[] }[],
  fontBytes?: ArrayBuffer,
): Promise<Blob> {
  const doc = await PDFDocument.create();
  doc.registerFontkit(fontkit);

  // Embed font — use provided CJK font or fall back to Helvetica
  let cjkFont = await doc.embedFont(StandardFonts.Helvetica);
  if (fontBytes) {
    cjkFont = await doc.embedFont(fontBytes, { subset: true });
  }

  // Group cards into pages (2 per page: left + right)
  const pages: { left?: typeof cards[0]; right?: typeof cards[0] }[] = [];
  for (const c of cards) {
    if (c.zone === "left") {
      pages.push({ left: c });
    } else {
      const lastPage = pages[pages.length - 1];
      if (lastPage && !lastPage.right) {
        lastPage.right = c;
      } else {
        pages.push({ right: c });
      }
    }
  }

  for (const pageCards of pages) {
    const page = doc.addPage([PW, PH]);

    // Title
    const titleSize = TITLE_FONT_SIZE_IN * PPI;
    page.drawText("貼文切角建議", {
      x: inPt(TITLE_POS.x),
      y: flipY(TITLE_POS.y, TITLE_FONT_SIZE_IN),
      size: titleSize,
      font: cjkFont,
      color: hexToRgb(TITLE_COLOR),
    });

    for (const entry of [pageCards.left, pageCards.right]) {
      if (!entry) continue;
      const { card, zone: zoneName, imageUrls } = entry;
      const zl = zoneName === "left" ? LEFT_ZONE : RIGHT_ZONE;
      const hasOrange = !!card.techLabel;
      const pillSize = PILL_FONT_SIZE_IN * PPI;

      // Images
      const imgX = parseIn(zl.imagePos[0]);
      const imgY = parseIn(zl.imagePos[1]);
      const imgW = parseIn(zl.imageSize[0]);
      const imgH = parseIn(zl.imageSize[1]);
      const n = imageUrls.length;
      const cols = n <= 1 ? 1 : n <= 2 ? 2 : n <= 4 ? 2 : 3;
      const rows = Math.ceil(n / cols);
      const gap = 0.06;
      const cellW = (imgW - gap * (cols - 1)) / cols;
      const cellH = (imgH - gap * (rows - 1)) / rows;

      for (let i = 0; i < n; i++) {
        try {
          const resp = await fetch(imageUrls[i]!);
          const buf = await resp.arrayBuffer();
          const bytes = new Uint8Array(buf);
          const isPng = bytes[0] === 0x89 && bytes[1] === 0x50;
          const img = isPng ? await doc.embedPng(buf) : await doc.embedJpg(buf);
          const col = i % cols;
          const row = Math.floor(i / cols);
          const cx = imgX + col * (cellW + gap);
          const cy = imgY + row * (cellH + gap);
          // Aspect-ratio fit within cell
          const imgAspect = img.width / img.height;
          const cellAspect = cellW / cellH;
          let dw: number, dh: number;
          if (imgAspect > cellAspect) { dw = cellW; dh = cellW / imgAspect; }
          else { dh = cellH; dw = cellH * imgAspect; }
          const ox = cx + (cellW - dw) / 2;
          const oy = cy + (cellH - dh) / 2;
          page.drawImage(img, {
            x: inPt(ox), y: flipY(oy, dh),
            width: inPt(dw), height: inPt(dh),
          });
        } catch { /* skip broken images */ }
      }

      // Blue pill (rounded rect)
      const pills = pillLayout(zl, hasOrange);
      const bp = pills.blue;
      page.drawRectangle({
        x: inPt(bp.x), y: flipY(bp.y, bp.h),
        width: inPt(bp.w), height: inPt(bp.h),
        color: hexToRgb(BLUE_FILL),
        borderWidth: 0,
      });
      page.drawText(card.title, {
        x: inPt(bp.x) + 8, y: flipY(bp.y, bp.h) + inPt(bp.h) / 2 - pillSize / 3,
        size: pillSize, font: cjkFont, color: hexToRgb(PILL_TEXT_COLOR),
        maxWidth: inPt(bp.w) - 16,
      });

      // Orange pill
      if (hasOrange && pills.orange) {
        const op = pills.orange;
        page.drawRectangle({
          x: inPt(op.x), y: flipY(op.y, op.h),
          width: inPt(op.w), height: inPt(op.h),
          color: hexToRgb(ORANGE_FILL), borderWidth: 0,
        });
        page.drawText(card.techLabel!, {
          x: inPt(op.x) + 8, y: flipY(op.y, op.h) + inPt(op.h) / 2 - pillSize / 3,
          size: pillSize, font: cjkFont, color: hexToRgb(PILL_TEXT_COLOR),
          maxWidth: inPt(op.w) - 16,
        });
      }

      // Text box
      const tbPos = hasOrange ? zl.textboxPos : zl.textboxPosNoOrange;
      const txX = parseIn(tbPos[0]);
      let curY = parseIn(tbPos[1]);
      const lineH = PILL_FONT_SIZE_IN * 1.4;

      const lines: string[] = [` - 規格：${card.spec}`];
      if (card.topic) lines.push(` - 題目：${card.topic}`);
      // For description, flatten highlights (pdf-lib can't mix fonts inline easily)
      const descParts = parseDesc(card.description);
      lines.push(` - 說明：${descParts.map(p => p.text).join("")}`);

      for (const line of lines) {
        page.drawText(line, {
          x: inPt(txX), y: flipY(curY, PILL_FONT_SIZE_IN),
          size: pillSize, font: cjkFont, color: hexToRgb(BODY_COLOR),
          maxWidth: inPt(parseIn((hasOrange ? zl.textboxSize : zl.textboxSizeNoOrange)[0])),
        });
        curY += lineH;
      }
    }
  }

  const bytes = await doc.save();
  return new Blob([bytes as unknown as BlobPart], { type: "application/pdf" });
}

// ---- Approach 2: Canvas → pdf-lib (raster, from preview HTML) ----

export async function exportPdfFromPreview(
  previewSlot: HTMLElement,
): Promise<Blob> {
  const slideDiv = previewSlot.querySelector(".card-preview-wrapper > div") as HTMLElement | null;
  if (!slideDiv) throw new Error("請先產生預覽");

  // Temporarily render at full size for capture
  const origTransform = slideDiv.style.transform;
  slideDiv.style.transform = "none";
  const w = Math.round(PAGE_WIDTH_IN * 96);
  const h = Math.round(PAGE_HEIGHT_IN * 96);

  const canvas = new OffscreenCanvas(w * 2, h * 2);
  const ctx = canvas.getContext("2d")!;
  ctx.scale(2, 2);
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, w, h);

  // Draw each child element — simplified raster capture via svg foreignObject
  const svgData = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">
    <foreignObject width="100%" height="100%">
      ${new XMLSerializer().serializeToString(slideDiv)}
    </foreignObject>
  </svg>`;
  const svgBlob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
  const url = URL.createObjectURL(svgBlob);
  const img = await createImageBitmap(svgBlob).catch(() => null);
  URL.revokeObjectURL(url);
  slideDiv.style.transform = origTransform;

  if (!img) throw new Error("無法擷取預覽。請使用向量 PDF 匯出。");

  ctx.drawImage(img, 0, 0, w, h);
  img.close();

  const pngBlob = await canvas.convertToBlob({ type: "image/png" });
  const doc = await PDFDocument.create();
  const pdfImage = await doc.embedPng(await pngBlob.arrayBuffer());
  const page = doc.addPage([PW, PH]);
  page.drawImage(pdfImage, { x: 0, y: 0, width: PW, height: PH });

  const bytes = await doc.save();
  return new Blob([bytes as unknown as BlobPart], { type: "application/pdf" });
}
